"use client";

import React, { useState, useEffect } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Building2, Briefcase, Handshake, Gift, Star, MessageSquare, ClipboardCheck, ChevronRight, ChevronLeft, Loader, CircleCheck } from "lucide-react";
const ALL_STEPS = [{
  id: "basic-info",
  title: "Basic Information",
  icon: Building2,
  fields: ["businessName", "email", "phone", "website"]
}, {
  id: "business-details",
  title: "Business Details",
  icon: Briefcase,
  fields: ["category", "description"]
}, {
  id: "involvement",
  title: "Network Involvement",
  icon: Handshake,
  fields: ["involvement_options"]
}, {
  id: "discount-offer",
  title: "Discount Offer",
  icon: Gift,
  fields: ["discount_details"],
  condition: (vals: any) => vals?.involvement_options?.includes("Offer Discounts to Members")
}, {
  id: "sponsorship",
  title: "Sponsorship",
  icon: Star,
  fields: ["sponsorship_level"],
  condition: (vals: any) => vals?.involvement_options?.includes("Explore Sponsorship")
}, {
  id: "additional-info",
  title: "Additional Info",
  icon: MessageSquare,
  fields: ["additional_info"]
}, {
  id: "review",
  title: "Review & Submit",
  icon: ClipboardCheck,
  fields: []
}];
const INVOLVEMENT_OPTIONS = [{
  id: "Attend Events",
  label: "Attend Networking Events"
}, {
  id: "Offer Discounts to Members",
  label: "Offer Discounts to Members"
}, {
  id: "Explore Sponsorship",
  label: "Explore Sponsorship Opportunities"
}, {
  id: "Host Events",
  label: "Host a Network Event"
}];
const CATEGORIES = ["Accounting & Finance", "Construction & Trades", "Consulting", "Education", "Health & Wellness", "Hospitality & Tourism", "IT & Technology", "Legal Services", "Marketing & Media", "Real Estate", "Retail", "Other"];
const SPONSORSHIP_LEVELS = [{
  id: "platinum",
  label: "Platinum Sponsor ($5,000+)",
  desc: "Premium branding across all network channels and events."
}, {
  id: "gold",
  label: "Gold Sponsor ($2,500+)",
  desc: "Prominent logo placement and event speaking opportunities."
}, {
  id: "silver",
  label: "Silver Sponsor ($1,000+)",
  desc: "Logo on website and recognition at quarterly events."
}, {
  id: "bronze",
  label: "Bronze Sponsor ($500+)",
  desc: "Directory listing highlight and social media shoutout."
}];
function MultiStepFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const formValues = useWatch({
    control
  });
  const [currentStepId, setCurrentStepId] = useState(ALL_STEPS[0].id);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const activeSteps = ALL_STEPS.filter(step => !step.condition || step.condition(formValues));
  const currentStepIndex = activeSteps.findIndex(s => s.id === currentStepId);
  const currentStepData = activeSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === activeSteps.length - 1;
  const progressPercentage = (currentStepIndex + 1) / activeSteps.length * 100;
  const handleNext = async () => {
    if (!currentStepData) return;
    const isStepValid = await trigger(currentStepData.fields);
    if (isStepValid && !isLastStep) {
      setDirection("forward");
      setCurrentStepId(activeSteps[currentStepIndex + 1].id);
      window.scrollTo({
        top: document.getElementById('business-network-multi-step-form')?.offsetTop,
        behavior: 'smooth'
      });
    }
  };
  const handlePrev = () => {
    if (!isFirstStep) {
      setDirection("backward");
      setCurrentStepId(activeSteps[currentStepIndex - 1].id);
      window.scrollTo({
        top: document.getElementById('business-network-multi-step-form')?.offsetTop,
        behavior: 'smooth'
      });
    }
  };
  if (isSubmitted && !submitError) {
    return <Card className="w-full max-w-3xl mx-auto border-primary/20 shadow-lg">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <CircleCheck className="size-10 text-primary" />
          </div>
          <CardTitle className="text-3xl font-bold">Application Received!</CardTitle>
          <CardDescription className="text-lg max-w-md mx-auto">
            {successMessage}
          </CardDescription>
          <Button onClick={resetForm} variant="outline" className="mt-8">
            Submit Another Application
          </Button>
        </CardContent>
      </Card>;
  }
  const StepIcon = currentStepData?.icon || Building2;
  return <Card className="w-full max-w-3xl mx-auto shadow-xl border-border/50 overflow-hidden">
      <CardHeader className="bg-muted/30 border-b border-border/50 pb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <StepIcon className="size-6" />
              </div>
              {currentStepData?.title}
            </CardTitle>
            <CardDescription className="mt-2 text-base">
              Step {currentStepIndex + 1} of {activeSteps.length}
            </CardDescription>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm font-medium text-muted-foreground">
            {Math.round(progressPercentage)}% Completed
          </div>
        </div>
        <Progress value={progressPercentage} className="h-2" />
      </CardHeader>

      <CardContent className="pt-8 pb-6 min-h-[400px]">
        <Form onSubmit={handleSubmit}>
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            
            {}
            {currentStepId === "basic-info" && <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="businessName" rules={{
              required: "Business Name is required"
            }} render={({
              field
            }) => <FormItem className="md:col-span-2">
                      <FormLabel>Business Name <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Corporation" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel>Contact Email <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="contact@acme.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="phone" rules={{
              required: "Phone number is required"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel>Phone Number <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="(555) 123-4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="website" render={({
              field
            }) => <FormItem className="md:col-span-2">
                      <FormLabel>Website URL</FormLabel>
                      <FormControl>
                        <Input type="url" placeholder="https://www.acme.com" {...field} />
                      </FormControl>
                      <FormDescription>Optional, but recommended.</FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "business-details" && <div className="space-y-6">
                <FormField control={control} name="category" rules={{
              required: "Please select an industry category"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel>Industry Category <span className="text-destructive">*</span></FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full md:w-[300px]">
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat, i) => <SelectItem key={cat} value={cat} data-index={i}>{cat}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>} />
                <FormField control={control} name="description" rules={{
              required: "Please provide a brief description",
              minLength: {
                value: 20,
                message: "Description must be at least 20 characters"
              }
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel>Business Description <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Textarea placeholder="Tell us about your products, services, and target audience..." className="min-h-[150px] resize-y" {...field} />
                      </FormControl>
                      <FormDescription>
                        This will be used for your directory listing if approved.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "involvement" && <div className="space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium">How would you like to participate?</h3>
                  <p className="text-sm text-muted-foreground">Select all that apply. Your selections will customize the rest of this form.</p>
                </div>
                <FormField control={control} name="involvement_options" rules={{
              required: "Please select at least one option"
            }} render={() => <FormItem>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {INVOLVEMENT_OPTIONS.map((option, i) => <FormField key={option.id} control={control} name="involvement_options" render={({
                  field
                }) => {
                  return <FormItem data-index={i} className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow-sm transition-colors hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                                  <FormControl>
                                    <Checkbox checked={field.value?.includes(option.id)} onCheckedChange={checked => {
                        const currentValue = field.value || [];
                        return checked ? field.onChange([...currentValue, option.id]) : field.onChange(currentValue.filter((value: string) => value !== option.id));
                      }} />
                                  </FormControl>
                                  <FormLabel className="font-medium cursor-pointer flex-1 leading-snug">
                                    {option.label}
                                  </FormLabel>
                                </FormItem>;
                }} />)}
                      </div>
                      <FormMessage className="mt-4" />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "discount-offer" && <div className="space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium">Member-to-Member Discount</h3>
                  <p className="text-sm text-muted-foreground">
                    Offering a discount is a great way to attract business from other network members.
                  </p>
                </div>
                <FormField control={control} name="discount_details" rules={{
              required: "Please describe the discount you'd like to offer"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel>Discount Details <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Textarea placeholder="e.g., 15% off all consulting services for active network members..." className="min-h-[120px]" {...field} />
                      </FormControl>
                      <FormDescription>
                        Be specific about what is included and any terms or conditions.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "sponsorship" && <div className="space-y-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium">Sponsorship Opportunities</h3>
                  <p className="text-sm text-muted-foreground">
                    Elevate your brand visibility by sponsoring our network events and initiatives.
                  </p>
                </div>
                <FormField control={control} name="sponsorship_level" rules={{
              required: "Please select a sponsorship level of interest"
            }} render={({
              field
            }) => <FormItem className="space-y-4">
                      <FormControl>
                        <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid gap-4">
                          {SPONSORSHIP_LEVELS.map((level, i) => <FormItem key={level.id} data-index={i} className="flex items-start space-x-3 space-y-0 rounded-md border p-4 shadow-sm transition-colors hover:bg-accent/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
                              <FormControl>
                                <RadioGroupItem value={level.id} className="mt-1" />
                              </FormControl>
                              <div className="space-y-1">
                                <FormLabel className="font-medium cursor-pointer text-base">
                                  {level.label}
                                </FormLabel>
                                <FormDescription className="text-sm">
                                  {level.desc}
                                </FormDescription>
                              </div>
                            </FormItem>)}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "additional-info" && <div className="space-y-6">
                <FormField control={control} name="additional_info" render={({
              field
            }) => <FormItem>
                      <FormLabel>Anything else we should know?</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Questions, comments, or specific goals for joining the network..." className="min-h-[150px]" {...field} />
                      </FormControl>
                      <FormDescription>Optional.</FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {}
            {currentStepId === "review" && <div className="space-y-8">
                <div className="mb-2">
                  <h3 className="text-lg font-medium">Review Your Application</h3>
                  <p className="text-sm text-muted-foreground">
                    Please review your information before submitting. You can go back to make changes if needed.
                  </p>
                </div>

                <div className="space-y-6 rounded-lg border bg-muted/20 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Business Name</h4>
                      <p className="font-medium">{formValues.businessName || "—"}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Category</h4>
                      <p className="font-medium">{formValues.category || "—"}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Contact</h4>
                      <p className="font-medium">{formValues.email}</p>
                      <p className="text-sm text-muted-foreground">{formValues.phone}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Website</h4>
                      <p className="font-medium">{formValues.website || "—"}</p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Involvement Interests</h4>
                    {formValues.involvement_options?.length > 0 ? <ul className="list-disc list-inside space-y-1">
                        {formValues.involvement_options.map((opt: string, i: number) => <li key={i} data-index={i} className="font-medium text-sm">{opt}</li>)}
                      </ul> : <p className="text-sm italic text-muted-foreground">null selected</p>}
                  </div>

                  {formValues.involvement_options?.includes("Offer Discounts to Members") && <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Discount Offer</h4>
                        <p className="text-sm whitespace-pre-wrap">{formValues.discount_details || "—"}</p>
                      </div>
                    </>}

                  {formValues.involvement_options?.includes("Explore Sponsorship") && <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Sponsorship Interest</h4>
                        <p className="font-medium capitalize">{formValues.sponsorship_level || "—"} Level</p>
                      </div>
                    </>}
                </div>

                {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-start gap-2">
                    <div className="mt-0.5"></div>
                    <p>{submitError}</p>
                  </div>}
              </div>}

          </div>
        </Form>
      </CardContent>

      <CardFooter className="bg-muted/10 border-t border-border/50 px-6 py-4 flex items-center justify-between">
        <Button type="button" variant="outline" onClick={handlePrev} disabled={isFirstStep || isSubmitting} className={isFirstStep ? "invisible" : ""}>
          <ChevronLeft className="mr-2 size-4" />
          Previous
        </Button>

        {!isLastStep ? <Button type="button" onClick={handleNext}>
            Next Step
            <ChevronRight className="ml-2 size-4" />
          </Button> : <Button type="button" onClick={handleSubmit} disabled={isSubmitting} className="min-w-[140px]">
            {isSubmitting ? <>
                <Loader className="mr-2 size-4 animate-spin" />
                Submitting...
              </> : <>
                Submit Application
                <CircleCheck className="ml-2 size-4" />
              </>}
          </Button>}
      </CardFooter>
    </Card>;
}
export default function BusinessNetworkMultiStepForm() {
  return <section id="business-network-multi-step-form" className="w-full py-16 md:py-24 bg-background">
      <div className="container px-4 md:px-6 mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-foreground">
            Join the Business Network
          </h2>
          <p className="text-lg text-muted-foreground">
            Complete the application below to become a member. Your involvement helps strengthen our local business community.
          </p>
        </div>

        <FormProvider formId="birks_business_network_form" sectionName="business-network-multi-step-form" defaultValues={{
        businessName: "",
        email: "",
        phone: "",
        website: "",
        category: "",
        description: "",
        involvement_options: [],
        discount_details: "",
        sponsorship_level: "",
        additional_info: ""
      }} successMessage="Thank you for applying to the Business Network! Our team will review your application and contact you shortly.">
          <MultiStepFormContent />
        </FormProvider>
      </div>
    </section>;
}